import React from "react";
import {
  Box,
  IconButton,
  Select,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { tokens } from "../../theme";

interface PaginationProps {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = totalCount === 0 ? 0 : currentPage * pageSize + 1;
  const endItem = Math.min((currentPage + 1) * pageSize, totalCount);
  const isPrevDisabled = currentPage === 0;
  const isNextDisabled = currentPage >= totalPages - 1 || totalPages === 0;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        justifyContent: "flex-end",
        mt: 2,
        px: 2,
        py: 1,
      }}
    >
      {/* Rows per page selector */}
      <Typography
        variant="body2"
        sx={{ color: colors.grey[300] || theme.palette.text.secondary }}
      >
        Rows per page:
      </Typography>
      <Select
        value={pageSize}
        onChange={(e) => onPageSizeChange(Number(e.target.value))}
        size="small"
        sx={{
          color: colors.grey[100] || theme.palette.text.primary,
          ".MuiOutlinedInput-notchedOutline": {
            borderColor: colors.primary[800] || theme.palette.divider,
          },

          ".MuiSvgIcon-root": {
            color: colors.grey[100] || theme.palette.action.active,
          },
        }}
      >
        {[10, 20, 50, 100].map((size) => (
          <MenuItem key={size} value={size}>
            {size}
          </MenuItem>
        ))}
      </Select>

      {/* Page range display */}
      <Typography
        variant="body2"
        sx={{
          color: colors.grey[100] || theme.palette.text.primary,
          minWidth: "70px", // Give it a fixed width to prevent jumping
          textAlign: "center",
        }}
      >
        {startItem}-{endItem} of {totalCount}
      </Typography>

      {/* Pagination navigation buttons */}
      <IconButton
        onClick={() => onPageChange(currentPage - 1)}
        disabled={isPrevDisabled}
        sx={{
          color: colors.grey[100] || theme.palette.action.active,
          "&.Mui-disabled": {
            color: colors.grey[700] || theme.palette.action.disabled,
          },
        }}
      >
        <ArrowBackIosNewIcon fontSize="small" />
      </IconButton>

      <IconButton
        onClick={() => onPageChange(currentPage + 1)}
        disabled={isNextDisabled}
        sx={{
          color: colors.grey[100] || theme.palette.action.active,
          "&.Mui-disabled": {
            color: colors.grey[700] || theme.palette.action.disabled,
          },
        }}
      >
        <ArrowForwardIosIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default Pagination;
